import { useEffect, useRef, useState } from 'react';
import { toPng } from 'html-to-image';
import { useI18n, useLogger, useSelector } from '~Hooks';
import useCheckPrinterImageSupport from '~Hooks/useCheckPrinterImageSupport';
import { useStorage } from '~Components/Storage';

interface Params {
  /** The order id this kiosk session is placing; guards against reprinting another session's slip. */
  orderId: string | null;
  /** Optional line printed under the QR code, already run through i18n. */
  extraMessage?: string;
}

/**
 * Watches the receipt-print slot in the store and prints the order-number slip for `orderId`.
 * Printers with image support get a rasterized React slip (mount it inside `slipRef`);
 * other printers get the plain text rows. Returns the order number to render while pending.
 */
const useOrderNumberSlipPrint = ({ orderId, extraMessage }: Params) => {
  const logger = useLogger('order-id-master');
  const print = useSelector((state) => state.POSReceiptPrints);
  const order = useSelector((state) => print && state.POSOrders.find((o) => o.id === print.id));
  const printers = useSelector((state) => state.printers);
  const bongPrintingHalted = useSelector((state) => state.BongPrintingHalted);
  const { i18n } = useI18n();
  const [storage] = useStorage();
  const { checkImagePrintingSupported } = useCheckPrinterImageSupport();
  const slipRef = useRef<HTMLDivElement | null>(null);
  /**
   * POS collect mode ("Samla"): every order in the session shares one order number, so the
   * customer gets a single slip, printed when collecting is turned off after the last order.
   * Holds that shared number while collecting is on.
   */
  const collectOrderNumberRef = useRef<string | null>(null);

  /** Order number waiting to be rasterized and printed; null when idle. */
  const [pendingOrderNumber, setPendingOrderNumber] = useState<string | null>(null);

  /** Queue a slip when the store asks us to print for this session's order. */
  useEffect(() => {
    // Kiosk: only when the customer declined a receipt. POS setting printOrderSlip: every purchase.
    if (
      !print ||
      print.id !== orderId ||
      !order?.orderNumber ||
      (!order.noReceipt && !storage.printOrderSlip)
    ) {
      return;
    }

    // Collecting: hold the slip until collecting ends (see the effect below)
    if (bongPrintingHalted) {
      logger.info('Collecting, holding order number slip until collecting ends', {
        orderNumber: order.orderNumber,
      });
      collectOrderNumberRef.current = order.orderNumber;
      return;
    }

    queueSlip(order.orderNumber);
  }, [print, order, orderId, extraMessage]);

  /** Collecting turned off: print the single slip for the collected orders. */
  useEffect(() => {
    if (bongPrintingHalted || !collectOrderNumberRef.current) return;

    queueSlip(collectOrderNumberRef.current);
    collectOrderNumberRef.current = null;
  }, [bongPrintingHalted]);

  /** Prints as image if the primary printer supports it, otherwise as text. */
  const queueSlip = (rawOrderNumber: string) => {
    const orderNumber = rawOrderNumber.padStart(3, '0');
    const primaryPrinter = printers.find((printer) => printer.primary);
    const imagePrintingSupported = checkImagePrintingSupported(primaryPrinter?.model || '');

    logger.info('Queueing order number slip', {
      orderNumber,
      printerModel: primaryPrinter?.model,
      imagePrintingSupported,
    });

    if (imagePrintingSupported) {
      setPendingOrderNumber(orderNumber);
      return;
    }

    printAsText(orderNumber);
  };

  /** Once the slip is mounted, rasterize it and send the image to the primary printer. */
  useEffect(() => {
    if (!pendingOrderNumber) return;

    printAsImage(pendingOrderNumber).finally(() => setPendingOrderNumber(null));
  }, [pendingOrderNumber]);

  /** Text fallback for printers that cannot print images. */
  const printAsText = (orderNumber: string) => {
    const message = [
      { content: ' ' },
      { content: ' ' },
      { content: ' ' },
      { content: ' ' },
      { content: ' ' },
      { content: `  ${i18n('SSK.YourOrderNumber')}  `, fontSize: 24 },
      { content: ` ${orderNumber} `, fontSize: 96 },
      { content: ' ' },
      ...(extraMessage ? [{ content: extraMessage, fontSize: 16 }, { content: ' ' }] : []),
      { content: ' ' },
      { content: ' ' },
      { content: ' ' },
      { content: i18n('SSK.OrderNumber.PoweredBy'), fontSize: 12 },
    ];

    logger.info('Printing order number slip as text', { orderNumber });

    window.printerManager?.initiatePrint({
      data: message,
      printType: 'EPOS_ORDER_NUMBER',
    });
  };

  /** Rasterizes the mounted slip element (same approach as BongMaster) and prints it. */
  const printAsImage = async (orderNumber: string) => {
    const slipElement = slipRef.current;

    if (!slipElement) {
      logger.error('No slip element found to print as image');
      return;
    }

    try {
      // Opacity must be 1 while rasterizing, otherwise the image comes out black
      slipElement.style.opacity = '1';

      // Let the browser lay out and paint the slip (incl. the QR svg) before cloning it
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

      const base64String = await toPng(slipElement, {
        pixelRatio: 2, // 300px element doubled to 600px, paper is 640px wide
        width: 300,
        skipFonts: true,
      });

      logger.info('Printing order number slip as image', {
        orderNumber,
        elementWidth: slipElement.offsetWidth,
        elementHeight: slipElement.offsetHeight,
        base64Length: base64String.length,
      });

      window.printerManager?.initiatePrint({
        printType: 'EPOS_ORDER_NUMBER',
        base64Image: base64String,
      });
    } catch (error) {
      logger.error('Error printing order number slip as image', { errorData: error });
    }
  };

  return { slipRef, pendingOrderNumber };
};

export default useOrderNumberSlipPrint;
